// 📄 services/planService.ts
//
// "Planes/Suscripciones" (Fase 5/5, 2026-08-26) — SOLO modelo de planes,
// SIN cobro real (ver proyecto_planes_suscripciones): un Admin de
// plataforma asigna un plan a cada usuario desde /admin > Organizaciones
// (LimitesPorUsuario), y el plan determina sus límites anti-abuso
// (equipos/proyectos/miembros/credenciales del Vault — los mismos 5 campos
// de siempre en users/{uid}, ver userService.ts) y qué funciones "premium" tiene
// habilitadas (features.slack, features.busquedaIA — las 2 únicas que
// consumen APIs externas de costo real: Slack webhooks y Gemini/Claude en
// busquedaInteligente.js). Asignar un plan solo fija el PUNTO DE PARTIDA:
// el admin puede seguir ajustando un límite puntual como excepción
// después (mismos inputs de siempre), sin que el plan lo bloquee.
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';

export interface PlanFeatures {
  slack: boolean;
  busquedaIA: boolean;
}

export interface Plan {
  id: string;
  nombre: string;
  descripcion: string;
  // null = sin límite (mismo criterio que el resto de la plataforma: el
  // campo se borra del doc, y firestore.rules lo trata como 999999 —
  // "ausente" ya significaba ilimitado desde el rediseño 2026-08-19).
  limiteEquipos: number | null;
  limiteProyectos: number | null;
  limiteMiembrosPorProyecto: number | null;
  limiteMiembrosPorOrganizacion: number | null;
  // Tope TOTAL agregado de credenciales que este usuario puede crear en el
  // Vault de proyectos, sumando TODOS los proyectos donde las cree (mismo
  // criterio "agregado, no por-contenedor" que limiteProyectos) — a
  // diferencia de los otros 4 campos, esto NO lo enforcea firestore.rules:
  // el Vault se crea vía credentialManager.js (Cloud Function con Admin
  // SDK, que bypassea las rules), así que el chequeo vive ahí mismo.
  limiteCredencialesVault: number | null;
  // Cuota MENSUAL de "API Tester" (ariba_ExecuteRequest + Buscar
  // Proveedores + Buscar Facturas comparten UN pozo, ver
  // functions/services/monthlyQuota.js) — a diferencia de los otros 5
  // campos, acá `null` NO se usa hoy: incluso Enterprise tiene un techo
  // real (decisión explícita del usuario, para no dejar costo externo sin
  // ningún tope aunque sea alto). El campo sigue siendo `number | null`
  // por si algún día hace falta un plan de verdad ilimitado.
  limiteApiTesterMensual: number | null;
  // Cuota MENSUAL de OneBot (Nexus AI) — mismo criterio que
  // limiteApiTesterMensual (`null` no se usa hoy, ni Enterprise queda sin
  // techo). A diferencia de API Tester, esto no se consume desde un
  // callable: el trigger functions/triggers/onebotGemini.js lo chequea
  // antes de gastar el LLM y responde en el propio chat si ya se llegó.
  limiteOneBotMensual: number | null;
  features: PlanFeatures;
}

export const PLAN_DEFAULT_ID = 'free';

export const PLANES: Plan[] = [
  {
    id: 'free',
    nombre: 'Free',
    descripcion: 'Punto de partida de toda cuenta nueva.',
    limiteEquipos: 1,
    limiteProyectos: 2,
    limiteMiembrosPorProyecto: 5,
    limiteMiembrosPorOrganizacion: 1,
    limiteCredencialesVault: 3,
    limiteApiTesterMensual: 50,
    limiteOneBotMensual: 30,
    features: { slack: false, busquedaIA: false }
  },
  {
    id: 'pro',
    nombre: 'Pro',
    descripcion: 'Para consultoras con varios proyectos y equipo activo.',
    limiteEquipos: 3,
    limiteProyectos: 20,
    limiteMiembrosPorProyecto: 15,
    limiteMiembrosPorOrganizacion: 10,
    limiteCredencialesVault: null,
    limiteApiTesterMensual: 1000,
    limiteOneBotMensual: 500,
    features: { slack: true, busquedaIA: true }
  },
  {
    id: 'enterprise',
    nombre: 'Enterprise',
    descripcion: 'Sin límites de equipos, proyectos ni miembros.',
    limiteEquipos: null,
    limiteProyectos: null,
    limiteMiembrosPorProyecto: null,
    limiteMiembrosPorOrganizacion: null,
    limiteCredencialesVault: null,
    limiteApiTesterMensual: 10000,
    limiteOneBotMensual: 5000,
    features: { slack: true, busquedaIA: true }
  }
];

export const getPlan = (planId: string | undefined): Plan =>
  PLANES.find((p) => p.id === planId) || PLANES.find((p) => p.id === PLAN_DEFAULT_ID)!;

/**
 * Asigna un plan a un usuario: escribe `planId`, `features`, y los 5
 * campos de límite (valor del plan, o borrados del doc si el plan trae
 * `null` = ilimitado). Requiere ser Admin de plataforma — igual que el
 * resto de updateUserLimite* en userService.ts, firestore.rules bloquea
 * esto para cualquier otro caller (ver match /users/{userId} → allow
 * update, rama isAdmin()).
 */
export const assignUserPlan = async (uid: string, planId: string): Promise<void> => {
  const plan = getPlan(planId);
  const ref = doc(db, 'users', uid);
  const payload: Record<string, any> = {
    planId: plan.id,
    features: plan.features,
    limiteEquipos: plan.limiteEquipos ?? deleteField(),
    limiteProyectos: plan.limiteProyectos ?? deleteField(),
    limiteMiembrosPorProyecto: plan.limiteMiembrosPorProyecto ?? deleteField(),
    limiteMiembrosPorOrganizacion: plan.limiteMiembrosPorOrganizacion ?? deleteField(),
    limiteCredencialesVault: plan.limiteCredencialesVault ?? deleteField(),
    limiteApiTesterMensual: plan.limiteApiTesterMensual ?? deleteField(),
    limiteOneBotMensual: plan.limiteOneBotMensual ?? deleteField()
  };
  await updateDoc(ref, payload);
};
